import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { productsAPI } from '../../services/api';
import { ProductOptionsSelector } from '../ProductOptionsSelector';
import type { SelectedOptionValue } from '../../types/productOptions';

/**
 * Add to cart, asking for required options first when the product has any.
 *
 * Five surfaces add straight to the cart without ever showing the options
 * picker — sticky ATC, frequently-bought-together, bundle-and-save,
 * recommendations and the cart-drawer upsell. Each builds its own stripped-down
 * product object from section config or a listing endpoint, so none of them
 * carries an `options` array to notice, and the shopper reached checkout with a
 * line that had chosen nothing. The server charges base price for those lines
 * on purpose (it cannot apply a default the shopper never saw), so every
 * upcharge bought through these paths was simply lost.
 *
 * They all know a product id, so the options are fetched on click — not on
 * render, and not at all for the common case where a click leads to a fetch
 * that comes back empty and the item is added exactly as before.
 *
 * A failed lookup adds the item anyway. Losing an upcharge is bad; blocking a
 * sale because a request failed is worse.
 */
export interface OptionsGateOptions {
  /** Passed through to CartContext.addItem — bundle ids, gift flags, discounts. */
  meta?: any;
  /** Runs once the item is actually in the cart, however that happened. */
  onAdded?: () => void;
}

interface Pending {
  product: any;
  quantity: number;
  options?: OptionsGateOptions;
}

export function useOptionsGate() {
  const { addItem } = useCart();
  // A queue, not a single slot: frequently-bought-together and bundle-and-save
  // add several products in one click. Everything without options goes straight
  // in; whatever needs a choice is asked for in turn, which in practice is zero
  // or one prompt.
  const [queue, setQueue] = useState<Pending[]>([]);
  const pending = queue[0] || null;
  const [selections, setSelections] = useState<SelectedOptionValue[]>([]);
  const [valid, setValid] = useState(true);
  const [busy, setBusy] = useState(false);

  const commit = useCallback(
    (p: Pending, chosen: SelectedOptionValue[]) => {
      addItem(
        p.product,
        p.quantity,
        chosen.length > 0 ? ({ options: chosen } as any) : undefined,
        p.options?.meta,
      );
      p.options?.onAdded?.();
    },
    [addItem],
  );

  const addToCart = useCallback(
    async (product: any, quantity = 1, options?: OptionsGateOptions) => {
      const id = Number(product?.id);
      if (!Number.isFinite(id) || id <= 0) {
        commit({ product, quantity, options }, []);
        return;
      }

      setBusy(true);
      try {
        const fetched = await productsAPI.getOptions(id);
        if (Array.isArray(fetched) && fetched.length > 0) {
          setQueue((q) => [...q, { product, quantity, options }]);
          return;
        }
      } catch {
        // fall through and add — never block a sale on this lookup
      } finally {
        setBusy(false);
      }

      commit({ product, quantity, options }, []);
    },
    [commit],
  );

  /** Add several at once — used by the bundle and cross-sell blocks. */
  const addManyToCart = useCallback(
    async (items: Array<{ product: any; quantity?: number; options?: OptionsGateOptions }>) => {
      for (const item of items) {
        await addToCart(item.product, item.quantity ?? 1, item.options);
      }
    },
    [addToCart],
  );

  /** Drop the current prompt and move to the next, if any. */
  const close = useCallback(() => {
    setQueue((q) => q.slice(1));
    setSelections([]);
    setValid(true);
  }, []);

  const optionsModal =
    pending && typeof document !== 'undefined'
      ? createPortal(
          <div
            // Above the cart drawer (z-[200]) so an upsell inside the drawer can
            // still ask its question.
            className="fixed inset-0 z-[220] flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
            role="dialog"
            aria-modal="true"
            aria-label={`Choose options for ${pending.product?.name || 'this product'}`}
            onClick={close}
          >
            <div
              className="w-full sm:max-w-lg max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5 sm:p-6"
              style={{
                backgroundColor: 'var(--card-bg, var(--color-surface, #ffffff))',
                color: 'var(--card-text, var(--color-text, #111827))',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <h2 className="text-lg font-semibold">
                  {pending.product?.name || 'Choose options'}
                </h2>
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close"
                  className="w-9 h-9 flex items-center justify-center rounded-full shrink-0"
                  style={{ color: 'var(--card-muted, #6b7280)' }}
                >
                  <X size={18} />
                </button>
              </div>

              <ProductOptionsSelector
                productId={Number(pending.product.id)}
                basePrice={(Number(pending.product.price) || 0) * 100}
                onSelectionChange={setSelections}
                onValidationChange={setValid}
              />

              <button
                type="button"
                disabled={!valid}
                onClick={() => {
                  commit(pending, selections);
                  close();
                }}
                className="w-full mt-6 py-4 rounded-xl font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: 'var(--accent, #111827)' }}
              >
                {valid ? 'Add to cart' : 'Please select required options'}
              </button>
            </div>
          </div>,
          document.body,
        )
      : null;

  return { addToCart, addManyToCart, optionsModal, optionsBusy: busy };
}
